import {html, TemplateResult} from "lit";

import {Article, Messages} from "../../../@types/online-reservation.js";
import '../product-section/product-section.js';
import '../reservation-badges/reservation-badges.js';
import checkIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_check.svg';
import {addPresentationToIcon} from "../../icon/icon-accessibility.js";
import {Step} from "./step.js";

interface ORResultOptions {
  article: Article;
  sizeDescription: string | undefined;
  storeLocationName: string;
  showSizeBadge: boolean;
  reservationSuccessful: boolean;
}

export class ORResult extends Step {
  private readonly reservationSuccessful: boolean;
  private readonly article: Article;
  private readonly sizeDescription: string | undefined;
  private readonly storeLocationName: string;
  private readonly showSizeBadge: boolean;

  constructor(
    private readonly messages: Messages,
    private readonly onNext: () => void,
    private readonly onRetry: () => void,
    options: ORResultOptions
  ) {
    super();
    this.article = options.article;
    this.sizeDescription = options.sizeDescription;
    this.storeLocationName = options.storeLocationName;
    this.showSizeBadge = options.showSizeBadge;
    this.reservationSuccessful = options.reservationSuccessful;
  }

  protected renderContent(): TemplateResult {
    return html`
      <div class="ents-or-reservation-closed">
        <div class="ents-or-reservation-closed-product-section">
          <product-section .article=${this.article}></product-section>
        </div>
        <reservation-badges
          .sizeDescription=${this.sizeDescription}
          .storeLocationName=${this.storeLocationName}
          .showSizeBadge=${this.showSizeBadge}
        ></reservation-badges>
        ${this.reservationSuccessful ? this.successContent() : this.failureContent()}
      </div>
    `;
  }

  protected renderFooter(): TemplateResult {
    return this.reservationSuccessful ? this.successFooter() : this.failureFooter();
  }

  protected renderTitle(): TemplateResult {
    return html`
      ${this.reservationSuccessful ? this.successTitle() : this.failureTitle()}
    `;
  }

  static renderButton(
    label: string,
    variant: 'primary' | 'outline',
    clickHandler: () => void,
    extraClasses = ''
  ) {
    return html`
      <button
        class="bdk-button ${extraClasses}"
        type="button"
        variant=${variant}
        size="medium"
        full-width
        @click=${clickHandler}
      >
        ${label}
      </button>
    `;
  }

  private successTitle() {
    return html`
      <div class="ents-or-reservation-closed-header">
        ${addPresentationToIcon(checkIcon())}
        <h2 id="or-step-title" class="ents-heading-medium ents-or-reservation-closed-header-text" tabindex="-1">${this.messages.success}</h2>
      </div>
    `;
  }

  private failureTitle() {
    return html`
      <div class="ents-or-reservation-closed-header">
        <h2 id="or-step-title" class="ents-heading-medium error" tabindex="-1">${this.messages.errorHeadline}</h2>
      </div>
    `;
  }

  private successContent() {
    return html`
      <span class="ents-copy">${this.messages.successText}</span>
    `;
  }

  private failureContent() {
    return html`
      <p class="ents-copy">${this.messages.error}</p>
    `;
  }

  private successFooter() {
    return html`
      <div class="ents-or-reservation-closed-button">
        ${ORResult.renderButton(this.messages.close, 'primary', this.onNext)}
      </div>
    `;
  }

  private failureFooter() {
    return html`
      <div class="ents-or-reservation-closed-button">
        ${ORResult.renderButton(this.messages.close, 'outline', this.onNext)}
        ${ORResult.renderButton(this.messages.tryAgain, 'primary', this.onRetry)}
      </div>
    `;
  }
}
